import Link from 'components/commercetools-ui/atoms/link';
import clsx from 'clsx';
import { isValidCategoryType } from 'components/commercetools-ui/organisms/header/header-navigation/helpers';
import { BackIcon, NextCategoryIcon } from 'components/icons';
import { trackMenuNavigation } from 'helpers/analytics/ga4Events';
import { cleanString, showBadge} from 'helpers/utils/util';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CategoryBarData, SubCategoryBarOptions, BadgeOptions } from 'shared/types/product/Category';

interface Props {
  category: CategoryBarData;
  anchorEl: HTMLElement | null;
  open: boolean;
  backButtonText: string;
  onLinkClick: () => void;
  /**
   * Whether to animate changes in dropdown position. In some cases, this should be
   * animated, for example when switching between categories, but in other cases it
   * should not be animated, for example when dragging the category slider
   */
  animatePosition: boolean;
}

export const CategoryDropdown: React.FC<Props> = ({
  category,
  anchorEl,
  open,
  backButtonText,
  onLinkClick,
  animatePosition,
}) => {
  const [nested, setNested] = useState<SubCategoryBarOptions | null>(null);
  const [isSubmenu, setIsSubmenu] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Make sure that the dropdown menu always appears below the anchor element (category button) and does not overflow the viewport
  // Updates every animation cycle to ensure it stays in the correct position even if the category bar has been scrolled
  useLayoutEffect(() => {
    if (!dropdownRef.current || !anchorEl) return;

    let frameId = 0;

    const updatePosition = () => {
      if (!dropdownRef.current) return;

      const anchorRect = anchorEl.getBoundingClientRect();
      const currentViewportWidth = window.innerWidth;
      const menuWidth = dropdownRef.current.offsetWidth;
      let nextLeft = anchorRect.left;

      if (nextLeft + menuWidth > currentViewportWidth - 16) {
        nextLeft = currentViewportWidth - menuWidth - 16;
      }

      if (nextLeft < 16) {
        nextLeft = 16;
      }

      dropdownRef.current.style.left = `${nextLeft}px`;
      dropdownRef.current.style.top = `${anchorRect.bottom + 8}px`;

      frameId = requestAnimationFrame(updatePosition);
    };

    updatePosition();

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [anchorEl]);

  useEffect(() => {
    setNested(null);
    setIsSubmenu(false);
  }, [category]);

  const isCategoryLink = isValidCategoryType(category?.categoryLinks);
  const isSubCategoryLink = isValidCategoryType(nested?.subcategoryUrls);
  const isCTALink = isValidCategoryType(category?.ctaLink);

  const getBadge = (badge: BadgeOptions) => (
    <span
      className="ml-8 truncate rounded-[34px] border-none px-8 leading-5"
      style={{
        fontSize: Number(badge.badgeTextSize),
        color: badge.badgeTextColor,
        backgroundColor: badge.badgeBgColor,
        fontWeight: badge.badgeFontWeight,
      }}
      title={badge.badgeText}
      data-ffid={`lbl-badge-${cleanString(
        badge.badgeText ?? '',
      )}`}
    >
      {badge.badgeText}
    </span>
  );

  return (
    <div
      className={clsx(
        // If you change the padding or height of this, make sure to edit the two child divs as well
        // They need to be kept in sync for the transition between them to work properly
        `fixed z-20 w-260 max-h-[60vh] overflow-hidden rounded-xl bg-white p-30 shadow-[0_0_8px_rgba(0,0,0,0.15)]`,
        `transform duration-300 ease-out ${animatePosition
          ? 'transition-[transform,opacity,left,top]'
          : 'transition-[transform,opacity]'}`,
        `${open
          ? 'pointer-events-auto translate-y-0 scale-100 opacity-100'
          : 'pointer-events-none translate-y-3 scale-95 opacity-0'
        }`
      )}
      ref={dropdownRef}
    >
      {/* MAIN LIST */}
      <div
        className={`w-full max-h-[calc(60vh-60px)] overflow-y-auto transition-all duration-300 ${isSubmenu
          ? 'pointer-events-none absolute inset-x-0 top-30 -translate-x-full opacity-0'
          : 'pointer-events-auto translate-x-0 opacity-100'
        }`}
      >
        <div className="mb-12 flex items-center space-x-2">
          <h2 className="text-14 font-bold text-[#018463]">
            {isCategoryLink ? (
              <Link
                link={category?.categoryLinks}
                data-ffid={`lnk-subcat-menu-${category?.categoryNameFFID}-link`}
                onClick={() => {
                  trackMenuNavigation(
                    'Category Bar',
                    category?.categoryName ?? '',
                    category?.categoryLinks?.link ?? '#',
                  );
                  onLinkClick();
                }}
              >
                {category?.categoryName}
              </Link>
            ) : (
              category?.categoryName
            )}
          </h2>
        </div>
        <ul data-ffid="ctr-sub-category-list">
          {Array.isArray(category?.subcategoryOptions) &&
            category.subcategoryOptions.map((item, index) => {
              const badge = item?.badgeConfigs?.[0];
              const badgeVisible = showBadge(badge?.badgeAvailableFrom, badge?.badgeExpiresOn);
              const menuItemClassName = 'flex w-full items-center justify-between py-6 text-12 text-primary';
              const menuItemFFID = `btn-sub-category-${item?.subcategoryNameFFID}-button`;
              const menuItem = (
                <>
                  <div className="flex items-center">
                    <span
                      data-ffid={`lbl-sub-category-${item?.subcategoryNameFFID}-label`}
                    >
                      {item.subcategoryName}
                    </span>
                    {badgeVisible && badge && getBadge(badge)}
                  </div>
                  <span data-ffid={`lbl-sub-category-arrow-${index}-icon`}>
                    <NextCategoryIcon />
                  </span>
                </>
              );

              return (
                <li
                  key={item.subcategoryName}
                  data-ffid={`lst-sub-category-${
                    item?.subcategoryNameFFID}`}
                >
                  {item.subSubCategoryOptions?.length ? 
                    // open nested list in menu
                    <button
                      type="button"
                      onClick={() => {
                          setNested(item);
                          setIsSubmenu(true);
                      }}
                      data-ffid={menuItemFFID}
                      className={menuItemClassName}
                    >
                      {menuItem}
                    </button>
                    : 
                    isValidCategoryType(item.subcategoryUrls) ?
                      // link to page
                      <Link
                        link={item.subcategoryUrls ?? ''}
                        className={menuItemClassName}
                        data-ffid={menuItemFFID}
                        considerQueryParams
                        onClick={() => {
                          trackMenuNavigation(
                            'Category Bar',
                            item.subcategoryName ?? '',
                            item.subcategoryUrls?.link ?? '#',
                          );
                          onLinkClick();
                        }}
                      > 
                        {menuItem}
                      </Link>
                      :
                      // static text
                      <span
                        className={menuItemClassName}
                        data-ffid={menuItemFFID}
                      >
                        {menuItem}
                      </span>
                    }
                </li>
              );
            })}
        </ul>
        <div className="mt-16">
          {isCTALink ? (
            <Link
              className="text-12 font-bold text-[#018463]"
              link={category?.ctaLink ?? '#'}
              role="menuitem"
              tabIndex={0}
              data-ffid={`lnk-subcat-sub-${
                category?.categoryNameFFID}`}
              onClick={() => {
                trackMenuNavigation(
                  'Category Bar',
                  category?.ctaText ?? '',
                  category?.ctaLink?.link ?? '#',
                );
                onLinkClick();
              }}
            >
              <span
                data-ffid={`lbl-category-cta-${
                  category?.categoryNameFFID }-label`}
              >
                {category?.ctaText}
              </span>
            </Link>
          ) : null}
        </div>
      </div>

      {/* NESTED LIST */}
      <div
        className={`w-full max-h-[calc(60vh-60px)] overflow-y-auto pl-16 transition-all duration-300 ${isSubmenu
          ? 'pointer-events-auto translate-x-0 opacity-100'
          : 'pointer-events-none absolute inset-x-0 top-30 translate-x-full opacity-0'
          } `}
      >
        <button
          type="button"
          className="relative -ml-18 pl-18 mb-10 flex items-center text-12 font-bold text-primary"
          onClick={() => setIsSubmenu(false)}
          data-ffid={`btn-cat-menu-back-button`}
        >
          <span className="absolute left-0 flex items-center justify-center">
            <BackIcon className="h-10" />
          </span>
          <span className="text-primary">
            {backButtonText}
          </span>
        </button>
        <div
          className="flex w-full items-center py-6 text-14 font-bold text-[#018463] "
          data-ffid={`lst-sub-sub-category-name-${nested?.subcategoryNameFFID}`}
        >
          {isSubCategoryLink ? (
            <Link
              link={nested?.subcategoryUrls ?? ''}
              data-ffid={`lnk-sub-sub-category-name-${nested?.subcategoryNameFFID}-link`}
              onClick={() => {
                trackMenuNavigation(
                  'Category Bar',
                  nested?.subcategoryName ?? '',
                  nested?.subcategoryUrls?.link ?? '#',
                );
                onLinkClick();
              }}
            >
              {nested?.subcategoryName}
            </Link>
          ) : (
            nested?.subcategoryName
          )}
        </div>
        <ul data-ffid="ctr-sub-sub-category-list">
          {Array.isArray(nested?.subSubCategoryOptions) && nested.subSubCategoryOptions?.map((child, idx: number) => {
            const badge = child?.badgeConfigs?.[0];
            const badgeVisible = showBadge(badge?.badgeAvailableFrom, badge?.badgeExpiresOn);
            return (child.subSubCategoryName ? (
              <li
                key={`${child.subSubCategoryName}-${idx}`}
                data-ffid={`lst-sub-sub-category-${child?.subSubcategoryNameFFID}`}
              >
                {child?.subSubCategoryLink ?
                  <Link
                    link={child.subSubCategoryLink}
                    className="flex w-full items-center py-6 text-12 text-primary"
                    data-ffid={`lnk-sub-sub-category-${child?.subSubcategoryNameFFID}-link`}
                    considerQueryParams
                    onClick={() => {
                      trackMenuNavigation(
                        'Category Bar',
                        child?.subSubCategoryName ?? '',
                        child.subSubCategoryLink?.link ?? '#',
                      );
                      onLinkClick();
                    }}
                  >
                    {child.subSubCategoryName}
                    {badgeVisible && badge && getBadge(badge)}
                  </Link>
                  :
                  <span
                    className="flex w-full items-center py-6 text-12 text-primary"
                    data-ffid={`lbl-sub-sub-category-${child?.subSubcategoryNameFFID}`}
                  >
                    {child.subSubCategoryName}
                    {badgeVisible && badge && getBadge(badge)}
                  </span>
                }
              </li>
            ) : null)
          }
          )}
        </ul>
      </div>
    </div>
  );
};
